import * as JobDetailActions from './job-detail.actions';
import * as JobDetailSelectors from './job-detail.selectors';
import * as moment from 'moment';

import {
	AccountingClient,
	BulkAddShiftsRequest,
	JobStatus,
	JobsClient,
} from 'src/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import {
	AddEditShiftDialogComponent,
	ShiftDialogData,
} from '../../shared/add-edit-shift-dialog/add-edit-shift-dialog.component';
import { QUOTE_STATUSES, getAllListArgs } from 'src/shared/constants';
import { Store, select } from '@ngrx/store';
import {
	exhaustMap,
	filter,
	map,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs/operators';
import { isEmpty, omit } from 'lodash';

import { AlertDialogComponent } from 'src/shared/alerts/alert-dialog/alert-dialog.component';
import { AuthService } from 'src/admin-app/shared/auth/auth.service';
import { BulkAddShiftsRequested } from '../../+state/job-shift/job-shift.actions';
import { DataPersistence } from '@nrwl/angular';
import { Injectable } from '@angular/core';
import { JobAssignmentsComponent } from '../../job-assignments/job-assignments.component';
import { JobDetailComponent } from '../job-detail.component';
import { JobDetailPartialState } from './job-detail.reducer';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RailProsError } from 'src/shared/models/rail-pros-error.model';
import { Roles } from 'src/shared/enums';
import { grabIdFromParams } from 'src/shared/utilities/utility-functions';
import { jobFormToJob } from '../job-detail.utility';
import { jobsFragments } from 'src/admin-app/jobs/jobs.routes';
import { notifyAfterSaving } from 'src/shared/utilities/custom-rxjs-operators';
import { quotesFragments } from 'src/admin-app/quotes/quotes.routes';
import { ReportsService } from 'src/admin-app/shared/services/reports.service';

@Injectable()
export class JobDetailEffects {
	selectJob$ = createEffect(() =>
		this.dataPersistence.navigation(JobDetailComponent, {
			run: (r: ActivatedRouteSnapshot, _state: JobDetailPartialState) => {
				const jobId = grabIdFromParams(r.paramMap);

				return JobDetailActions.jobSelected({
					jobId,
				});
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	selectJobForAssignments$ = createEffect(() =>
		this.dataPersistence.navigation(JobAssignmentsComponent, {
			run: (r: ActivatedRouteSnapshot, _state: JobDetailPartialState) => {
				const jobId = grabIdFromParams(r.paramMap);

				return JobDetailActions.jobSelectedForAssignments({
					jobId,
				});
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	navigateToJob$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(JobDetailActions.jobTurnedIntoJob),
				withLatestFrom(
					this.store.pipe(
						select(JobDetailSelectors.getSelectedJobId)
					),
					(_id, jobId) => jobId
				),
				filter((id) => id > 0),
				tap((jobId) => {
					this.router.navigate(jobsFragments.detail(jobId));
				})
			),
		{ dispatch: false }
	);

	loadJobFromStore$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				JobDetailActions.jobSelected,
				JobDetailActions.jobSelectedForAssignments
			),
			map((action) => action.jobId),
			filter((id) => id > 0),
			withLatestFrom(
				this.store.pipe(select(JobDetailSelectors.getSelectedJob)),
				(_id, job) => job
			),
			filter(Boolean),
			map((job) => JobDetailActions.jobLoadedFromStore({ job }))
		)
	);

	loadJobFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				JobDetailActions.jobSelected,
				JobDetailActions.jobSelectedForAssignments
			),
			map((action) => action.jobId),
			filter((id) => id > 0),
			switchMap((jobId) => this.jobsClient.getJob(jobId)),
			map((job) =>
				JobDetailActions.jobLoadedFromApi({
					job,
				})
			)
		)
	);

	getOtherChargeItems$ = createEffect(() =>
		this.actions$.pipe(
			ofType(JobDetailActions.jobSelected),
			map((action) => action.jobId),
			filter((id) => id > 0),
			filter<number>(Boolean),
			switchMap((id) => this.accountingClient.getOtherChargeItem(id)),
			map((otherCharges) =>
				JobDetailActions.otherChargeItemsLoaded({ otherCharges })
			)
		)
	);

	refreshOtherChargeItems$ = createEffect(() =>
		this.actions$.pipe(
			ofType(JobDetailActions.otherChargesSaved),
			withLatestFrom(
				this.store.pipe(select(JobDetailSelectors.getSelectedJobId)),
				(_, id) => id
			),
			switchMap((id) => this.accountingClient.getOtherChargeItem(id)),
			map((otherCharges) =>
				JobDetailActions.otherChargeItemsLoaded({ otherCharges })
			)
		)
	);

	guardAgainstIsStillQuote$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					JobDetailActions.jobLoadedFromApi,
					JobDetailActions.jobLoadedFromStore
				),
				filter(
					({ job }) =>
						QUOTE_STATUSES.includes(job.status) &&
						!(
							this.authService.userRoles.includes(Roles.Admin) ||
							this.authService.userRoles.includes(
								Roles.FieldDirector
							)
						)
				),
				tap(({ job }) =>
					this.router.navigate(quotesFragments.detail(job.id))
				)
			),
		{ dispatch: false }
	);

	requestRailroad$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				JobDetailActions.jobLoadedFromApi,
				JobDetailActions.jobLoadedFromStore
			),
			map((action) => action.job),
			filter((job) => !!job.railroad),
			map((job) => job.railroad.id),
			map((railroadId) =>
				JobDetailActions.railroadRequested({ railroadId })
			)
		)
	);

	requestCustomer$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				JobDetailActions.jobLoadedFromApi,
				JobDetailActions.jobLoadedFromStore
			),
			map((action) => action.job),
			filter((job) => !!job.customer),
			map((job) => job.customer.id),
			map((customerId) =>
				JobDetailActions.customerRequested({ customerId })
			)
		)
	);

	saveJob$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(JobDetailActions.saveRequested, {
			run: (
				action: ReturnType<typeof JobDetailActions.saveRequested>,
				_state: JobDetailPartialState
			) => {
				const job = jobFormToJob(action.formValue);

				return this.jobsClient.saveJob(job).pipe(
					map(() =>
						JobDetailActions.jobSaved({
							job,
						})
					)
				);
			},
			onError: (
				_action: ReturnType<typeof JobDetailActions.saveRequested>,
				error: any
			) => {
				return JobDetailActions.errorSavingJob({
					error,
				});
			},
		})
	);

	extendJobDates$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				JobDetailActions.shiftsAddedOutsideOfJobDates,
				JobDetailActions.ShiftsAddedFromAssignmentsOutsideOfJobDates
			),
			withLatestFrom(
				this.store.pipe(select(JobDetailSelectors.getSelectedJob)),
				(_, job) => job
			),
			exhaustMap((job) => this.jobsClient.saveJob(job)),
			tap(() =>
				this.dialog.open(AlertDialogComponent, {
					data: {
						message:
							'A shift was created outside the date range of this job,\
							so the start/end date of the job has been adjusted.',
						showFailureButton: false,
					},
				})
			),
			map(() => JobDetailActions.jobDatesExtended())
		)
	);

	cancelJob$ = createEffect(() =>
		this.actions$.pipe(
			ofType(JobDetailActions.userConfirmedCancelJob),
			map((action) => {
				const jobForm = action.jobForm;
				jobForm.generalInfo.status = JobStatus.Cancelled;
				return jobForm;
			}),
			map(jobFormToJob),
			exhaustMap((job) =>
				this.jobsClient
					.saveJob(job)
					.pipe(map(() => JobDetailActions.jobSaved({ job })))
			)
		)
	);

	completeJob$ = createEffect(() =>
		this.actions$.pipe(
			ofType(JobDetailActions.userConfirmedCompleteJob),
			map((action) => {
				const jobForm = action.jobForm;
				jobForm.generalInfo.status = JobStatus.Completed;
				jobForm.generalInfo.dateCompleted = moment.utc().toJSON();
				return jobForm;
			}),
			map(jobFormToJob),
			exhaustMap((job) =>
				this.jobsClient
					.saveJob(job)
					.pipe(map(() => JobDetailActions.jobSaved({ job })))
			)
		)
	);

	requestNewJobSetupForm$ = createEffect(() =>
		this.actions$.pipe(
			ofType(JobDetailActions.userConfirmedRequestNewJobSetupForm),
			map((action) => {
				const jobForm = action.jobForm;
				jobForm.generalInfo.jobSetupFormRefreshDate = moment
					.utc()
					.toJSON();
				return jobForm;
			}),
			map(jobFormToJob),
			exhaustMap((job) =>
				this.jobsClient
					.saveJob(job)
					.pipe(map(() => JobDetailActions.jobSaved({ job })))
			)
		)
	);

	throwIfError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					JobDetailActions.errorSavingJob,
					JobDetailActions.errorLoadingJob
				),
				map((action) => action.error),
				map((error) => {
					throw Error(error);
				})
			),
		{ dispatch: false }
	);

	closeAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(JobDetailActions.jobSaved),
				withLatestFrom(
					this.store.pipe(
						select(JobDetailSelectors.getCloseAfterSaving)
					),
					(_, shouldClose) => shouldClose
				),
				filter(Boolean),
				tap(() => this.router.navigate(jobsFragments.list))
			),
		{ dispatch: false }
	);

	confirmPrevailingWage$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(
			JobDetailActions.userConfirmedPrevailingWage,
			{
				run: (
					action: ReturnType<
						typeof JobDetailActions.userConfirmedPrevailingWage
					>,
					_state: JobDetailPartialState
				) => {
					const j = jobFormToJob(action.jobForm);

					if (
						!j.prevailingWageInfo ||
						isEmpty(omit(j.prevailingWageInfo, 'id'))
					) {
						throw new RailProsError(
							450,
							'',
							'Prevailing Wage Information must be set before it can be confirmed.'
						);
					}
					if (
						j.prevailingWageInfo.confirmedBy ||
						j.prevailingWageInfo.confirmedDate
					) {
						throw new RailProsError(
							450,
							'',
							'Prevailing Wage has already been confirmed.'
						);
					}
					return this.jobsClient.saveJob(j).pipe(
						exhaustMap((id) =>
							this.jobsClient.confirmPrevailingWages(id)
						),
						map((response) =>
							JobDetailActions.prevailingWagesConfirmed({
								prevailingWageInfo: {
									...j.prevailingWageInfo,
									...response,
								},
							})
						)
					);
				},
				onError: (
					_action: ReturnType<
						typeof JobDetailActions.userConfirmedPrevailingWage
					>,
					error: any
				) => {
					return JobDetailActions.errorSavingJob({
						error,
					});
				},
			}
		)
	);

	notifyAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(JobDetailActions.jobSaved),
				notifyAfterSaving(this.snackbar)
			),
		{ dispatch: false }
	);

	OpenAddShiftsDialog$ = createEffect(() =>
		this.actions$.pipe(
			ofType(JobDetailActions.openAddShiftDialog),
			withLatestFrom(
				this.store.pipe(select(JobDetailSelectors.getSelectedJob))
			),
			exhaustMap(([openDialog, job]) =>
				this.dialog
					.open(AddEditShiftDialogComponent, {
						minWidth: '20rem',
						data: {
							add: true,
							addSingle: false,
							delete: false,
							job: job,
							showServiceType:
								(job.offeredServices || []).length > 1 ||
								[
									...new Set(
										(job.contractedRates || [])
											.filter((r) => r.serviceType)
											.map((r) => r.serviceType.id)
									),
								].length > 1,
							initialStartDate: openDialog.startDate,
							initialEndDate: openDialog.endDate,
						} as ShiftDialogData,
					})
					.afterClosed()
			),
			filter(Boolean),
			map(
				(shifts: BulkAddShiftsRequest) =>
					new BulkAddShiftsRequested({ shifts })
			)
		)
	);

	loadQuoteLogsFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(JobDetailActions.quoteLogsRequested),
			withLatestFrom(
				this.store.pipe(select(JobDetailSelectors.getSelectedJobId)),
				(_, id) => id
			),
			filter<number>(Boolean),
			switchMap((jobId) =>
				this.jobsClient.getQuoteLogs({ ...getAllListArgs, jobId })
			),
			map((pagedQuoteLogResults) =>
				JobDetailActions.pagedQuoteLogsLoaded({
					pagedQuoteLogResults,
				})
			)
		)
	);

	generatePayrollMonthlyReportCsv$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					JobDetailActions.confirmedGeneratePayrollMonthlyReportCsv
				),
				tap(({ args }) =>
					this.reportsService
						.payrollMonthlyReportCsv(args)
						.subscribe()
				)
			),
		{ dispatch: false }
	);

	loadJobLogsFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(JobDetailActions.jobSelected),
			map((action) => action.jobId),
			filter<number>(Boolean),
			switchMap((jobId) => this.jobsClient.getJobLogs(jobId)),
			map((pagedJobLogResults) =>
				JobDetailActions.pagedJobLogsLoaded({
					pagedJobLogResults,
				})
			)
		)
	);

	refreshJobHistory$ = createEffect(() =>
		this.actions$.pipe(
			ofType(JobDetailActions.jobSaved),
			withLatestFrom(
				this.store.pipe(select(JobDetailSelectors.getSelectedJobId)),
				(_, id) => id
			),
			switchMap((jobId) => this.jobsClient.getJobLogs(jobId)),
			map((pagedJobLogResults) =>
				JobDetailActions.pagedJobLogsLoaded({
					pagedJobLogResults,
				})
			)
		)
	);

	constructor(
		private readonly actions$: Actions,
		private readonly dialog: MatDialog,
		private readonly store: Store<JobDetailPartialState>,
		private readonly accountingClient: AccountingClient,
		private readonly dataPersistence: DataPersistence<JobDetailPartialState>,
		private readonly jobsClient: JobsClient,
		private readonly snackbar: MatSnackBar,
		private readonly router: Router,
		private readonly authService: AuthService,
		private readonly reportsService: ReportsService
	) {}
}
